import React, { useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import SideBar from "../../components/SideBar";
import {
  formatCurrency,
  frenchDate,
  getDaysDurationBetweenDates,
  getDurationBetweenDates,
} from "../../variables/functions";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Divider,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  FilterOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import frFR from "antd/locale/fr_FR";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import jsPDF from "jspdf";
import { highlightMatch } from "../../variables/stringFunctions";
import { searchSinistre } from "../../hooks/useSearch";
import { drawHeaderSinistrePdfGenerator, generateSinistresExcel, sinistresPdfGenerator } from "./hooks/generatesHook";
import { styles } from "./hooks/listStyles";
import FiltersModal from "./components/list/FiltersModal";
import { allColumns, columns, stats, TAB_CONFIG, tabsDefinition } from "./components/utils/listData";
import { StatCard } from "../../components/statistics/StatCard";
import { SINISTER_COLORS } from "../../components/ui/colors";
import { LOGGED_USER } from "../../variables/constants";

const { Title, Text } = Typography;

const { RangePicker } = DatePicker;
const dateFormat = "DD/MM/YYYY";

const extractCreatorId = (sinistre) => {
  const candidate = sinistre?.createdBy ?? sinistre?.creator ?? sinistre?.createur;
  if (!candidate) return null;
  if (typeof candidate === "string") return candidate;
  if (candidate?.codeId) return candidate.codeId;
  if (candidate?.id) return candidate.id;
  return candidate;
};

const computeRedacteurDisplay = (row, namesMap) => {
  const creatorName = row?.createdBy && namesMap?.[row.createdBy];
  if (creatorName) return creatorName;
  if (row?.redacteurBase && row.redacteurBase !== "—") return row.redacteurBase;
  if (row?.createdBy) return row.createdBy;
  return "—";
};

// Tente d'extraire le nom du rédacteur du sinistre à partir des différentes formes possibles.
const resolveSinistreRedacteur = (sinistre) => {
  if (!sinistre) return "—";

  const candidates = [
    sinistre?.redacteur,
    sinistre?.redacteurSinistre,
    sinistre?.declarePar,
    sinistre?.declarantUtilisateur,
    sinistre?.utilisateur,
    sinistre?.user,
    sinistre?.createdBy,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }

    const person = candidate?.personne ?? candidate;
    const fullName = [person?.prenom, person?.nom].filter(Boolean).join(" ").trim();
    if (fullName) return fullName;

    const username = person?.username || person?.userName || person?.login;
    if (username) return username;
  }

  const declarant = [sinistre?.nomDeclarant, sinistre?.phoneDeclarant]
    .filter(Boolean)
    .join(" - ")
    .trim();
  return declarant || "—";
};

const ListSinistres = () => {
  const [data, setData] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingSinistres, setLoadingSinistres] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCompagnies, setSelectedCompagnies] = useState([]);
  const [membres, setMembres] = useState([]);
  const [branches, setBranches] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [excelPreviewUrl, setExcelPreviewUrl] = useState([]);
  const [count, setCount] = useState({});
  const [tabId, setTabId] = useState("1");
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [creatorNames, setCreatorNames] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (pagination, _filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setPagination(pagination);
    setSortedInfo(sorter);
  };

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = selectedYear !== new Date().getFullYear() || selectedMonths.length > 0;

  const handleDateChange = (_dates, dateStrings) => {
    const [sDate, eDate] = dateStrings;
    if (!sDate || !eDate) {
      setFilteredInfo((prev) => ({
        ...prev,
        startDate: undefined,
        endDate: undefined,
      }));
      fetchData({
        pagination,
        filters: { ...filteredInfo, startDate: undefined, endDate: undefined },
        tabKey: tabId,
      });
      return;
    }
    const [startDay, startMonth, startYear] = sDate.split("/");
    const [endDay, endMonth, endYear] = eDate.split("/");
    const startDate = `${startYear}-${startMonth}-${startDay}`;
    const endDate = `${endYear}-${endMonth}-${endDay}`;
    setFilteredInfo((prev) => ({ ...prev, startDate, endDate }));
    fetchData({ pagination, filters: { ...filteredInfo, startDate, endDate }, tabKey: tabId });
  };

  const filteringOnBranches = (branche) => {
    const nextFilters = { ...filteredInfo, branche: branche };
    setFilteredInfo(nextFilters);
    fetchData({ pagination, filters: nextFilters, tabKey: tabId });
  };

  const filteringOnCompanies = (companies) => {
    setSelectedCompagnies(companies);
    const nextFilters = { ...filteredInfo, compagnie: companies };
    setFilteredInfo(nextFilters);
    fetchData({ pagination, filters: nextFilters, tabKey: tabId });
  };

  const fetchData = async (params = {}) => {
    setLoadingSinistres(true);
    const activeTabKey = params.tabKey ?? tabId;
    const { url, statusFilter = [] } = TAB_CONFIG[activeTabKey] || TAB_CONFIG["1"];
    const providedFilters = params.filters ?? {};

    try {
      const response = await axiosInstance.get(url, {
        params: {
          page: (params.pagination?.current ?? pagination.current) - 1,
          size: params.pagination?.pageSize ?? pagination.pageSize,
          ...providedFilters,
          compagnie: providedFilters?.compagnie?.join(","),
          categorie: providedFilters?.categorie?.join(","),
          selectedYear,
          selectedMonths: selectedMonths ? selectedMonths.join(',') : null,
        },
      });

      const { content, totalElements } = response.data;
      // console.log("Une réponse", content);

      const rows = content.map((sinistre, idx) => {
        const etatCode = sinistre?.statutDossier;
        const normalizeEtat = (value) => {
          if (value === null || value === undefined) return "";
          if (typeof value === "number") return value.toString();
          if (typeof value === "string") {
            return value
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .trim()
              .toUpperCase();
          }
          return "";
        };
        const sanitizeEtat = (value) => normalizeEtat(value).replace(/[^A-Z0-9]/g, "");
        const sanitizedEtatCode = sanitizeEtat(etatCode);
        const pendingEtatCodes = ["A", "AVALIDER"];
        const refusedEtatCodes = ["RF", "REFUSE"];
        const aValider = pendingEtatCodes.includes(sanitizedEtatCode)
          || pendingEtatCodes.includes(sinistre?.sortDossier);
        const refused = refusedEtatCodes.includes(sinistre?.sortDossier)
          || refusedEtatCodes.includes(sinistre?.sortDossier);

        const numeroDisplay = (sinistre?.numeroSinistrem ?? sinistre?.numSinistre);
        const creatorId = extractCreatorId(sinistre);
        const redacteurBase = resolveSinistreRedacteur(sinistre);

        return {
          key: sinistre.codeId,
          id: idx,
          createdBy: creatorId,
          numeroPolice: sinistre.numeroPoliceM ? sinistre.numeroPoliceM : sinistre.numeroPoliceP,
          assure: sinistre?.prenom ? `${sinistre?.prenom} ${sinistre?.nom}` : `${sinistre?.nom}`,
          organisme: sinistre?.organisme,
          data: sinistre,
          dateSinistre: sinistre?.dateSinistre ? frenchDate(sinistre?.dateSinistre) : "-",
          dateDeclaration: sinistre?.dateDeclaration ? frenchDate(sinistre?.dateDeclaration) : frenchDate(sinistre?.createdAt),
          numeroSinistre: numeroDisplay,
          etat: sinistre?.etat,
          aValider,
          refused,
          sap: sinistre?.sap ? `${formatCurrency(sinistre?.sap)}` : 0,
          evaluation: sinistre?.evaluation ? `${formatCurrency(sinistre?.evaluation)}` : 0,
          duree: getDurationBetweenDates(
            sinistre?.dateSinistre,
            sinistre?.dateDeclaration ?? sinistre?.createdAt
          ),
          redacteurBase,
          redacteur: computeRedacteurDisplay(
            { createdBy: creatorId, redacteurBase },
            creatorNames
          ),
          daysDuree: getDaysDurationBetweenDates(
            sinistre?.dateSinistre,
            sinistre?.dateDeclaration ?? sinistre?.createdAt
          ),
        };
      });
      // console.log("Rows", rows);
      setData(rows);
      const effectiveTotal = statusFilter.length > 0 ? rows.length : totalElements;
      setPagination((prev) => ({
        ...(params.pagination ?? prev),
        total: effectiveTotal,
      }));
      await fetchCreatorNames(rows);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingSinistres(false);
    }
  };

  useEffect(() => {
    if (location.state?.goToTab) {
      setTabId(location.state.goToTab);
    }
  }, [location.state]);

  useEffect(() => {
    axiosInstance
      .get(`/api/sinistre/status-count`, {
        params: { selectedYear, selectedMonths: selectedMonths ? selectedMonths?.join(',') : null },
      })
      .then((res) => setCount(res.data))
      .catch((err) => console.log(err));

    axiosInstance
      .get(`/api/sinistre/non-valide/count`, {
        params: { selectedYear, selectedMonths: selectedMonths ? selectedMonths?.join(',') : null },
      })
      .then((res) =>
        setCount((prev) => ({
          ...prev,
          totalSinistresNonValides: res.data?.totalSinistresNonValides ?? 0,
        }))
      )
      .catch((err) => console.log(err));

    if (selectedDate || selectedCompagnies.length) {
      if (selectedDate) {
        const [startDate, endDate] = selectedDate;
        fetchData({
          pagination,
          filters: {
            ...filteredInfo,
            startDate,
            endDate,
            compagnie: selectedCompagnies,
          },
          tabKey: tabId,
        });
      } else {
        fetchData({
          pagination,
          filters: { ...filteredInfo, compagnie: selectedCompagnies },
          tabKey: tabId,
        });
      }
    } else {
      fetchData({ pagination, filters: filteredInfo, tabKey: tabId });
    }

  }, [currentPage, pageSize, selectedDate, tabId, selectedMonths, selectedYear]);

  useEffect(() => {
    axiosInstance
      .get(`/api/organisme/assurance`)
      .then((res) => {
        const membs = res.data.map((memb, index) => ({
          key: index,
          text: memb.raisonSociale,
          label: memb.raisonSociale,
          value: memb.code,
          data: memb,
        }));
        membs.push({
          key: "POOL",
          text: "POOL",
          label: "POOL TPVM-VT GIE",
          value: "00",
        });
        setMembres(membs);
      })
      .catch((err) => console.log(err));

    axiosInstance
      .get(`/api/branche`)
      .then((res) => {
        const brs = res.data.map((br, index) => ({
          key: index,
          label: br.nom,
          value: br.codeBranche,
          data: br,
        }));
        setBranches(brs);
      })
      .catch((err) => console.log(err));
  }, []);

  const getTabColumns = (key) => (["1", "2", "3", "4", "5"].includes(key) ? columns(navigate) : allColumns(navigate));
  const getTabDataSource = (key) => (key === "6" ? data.filter((r) => r.etat === 0) : data);

  const generateSinistresPdf = (sinistres) => {
    setLoadingSinistres(true);
    const doc = new jsPDF({
      orientation: "landscape", // ✅ PAYSAGE
      unit: "mm",
      format: "a4"
    });

    const grouped = {};

    const now = new Date();
    const formattedDateTime = now.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    drawHeaderSinistrePdfGenerator(doc, pageWidth, formattedDateTime);

    axiosInstance.get(`/api/sinistre/etat-cima-v2`)
      .then((response) => {
        const sinistres = response.data;
        setExcelPreviewUrl(response.data);
        const dataUri = sinistresPdfGenerator(sinistres, doc, grouped, formattedDateTime);
        setPdfPreviewUrl(dataUri);
        setLoadingSinistres(false);
      }).catch((error) => {
        console.error("Error fetching sinistres for PDF generation:", error);
        setLoadingSinistres(false);
      });

  }


  const applyFilters = () => {
    setShowFilters(false);
    // Ici vous pouvez ajouter la logique pour appliquer les filtres
    console.log('Filtres appliqués:', { selectedYear, selectedMonths });
  };

  const closePreview = () => setPdfPreviewUrl(null);
  const onTabsChange = (key) => {
    setTabId(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setCurrentPage(1);
  };

  const renderSearchResults = (results) => {
    return <div className="px-3 py-2">
      <h6 className="text-muted text-uppercase mb-2" />
      {results.map((item, index) => (
        <div
          key={index}
          onClick={() =>
            navigate("/details-sinistre", {
              state: { codeId: item.codeId },
            })
          }
          className="dropdown-item d-flex align-items-center gap-2 py-1"
          style={{ cursor: "pointer" }}
        >
          <span className="text-truncate">
            {[
              (item?.numeroSinistrem || item?.numSinistre),
              item?.numeroPoliceM
                ? item?.numeroPoliceM
                : item?.numeroPoliceP,
              frenchDate(item?.dateSinistre),
              item?.immatriculation,
              item?.nom,
              item?.prenom || "",
            ].map((text, i) => (
              <React.Fragment key={i}>
                {i > 0 && " - "}
                {highlightMatch(text?.toString(), searchValue)}
              </React.Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>;
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchSinistre(searchValue).then((res) => {
        setSearchResults(res.data.content);
      }).catch((err) => {
        console.log(err);
      });
    }
  }, [searchValue]);

  const fetchCreatorNames = async (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const missingIds = [...new Set(rows.map((r) => r.createdBy).filter(Boolean))]
      .filter((id) => !creatorNames[id]);
    if (!missingIds.length) return;

    try {
      const responses = await Promise.all(
        missingIds.map((id) =>
          axiosInstance
            .get(`/api/utilisateur/codeId/${id}`)
            .then((res) => {
              const user = res?.data;
              const fullname = [user?.prenom, user?.nom].filter(Boolean).join(" ").trim();
              return { id, name: fullname || user?.login || user?.username || id };
            })
            .catch(() => ({ id, name: id }))
        )
      );
      const namesMap = responses.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});
      setCreatorNames((prev) => ({ ...prev, ...namesMap }));
    } catch (err) {
      console.error("Erreur chargement rédacteurs sinistres:", err);
    }
  };

  useEffect(() => {
    if (!Object.keys(creatorNames).length) return;
    setData((prev) =>
      prev.map((row) => ({
        ...row,
        redacteur: computeRedacteurDisplay(row, creatorNames),
      }))
    );
  }, [creatorNames]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"SINISTRES"} />
        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* ── En-tête de page ───────────────────────────── */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 24,
                flexWrap: "wrap",
                gap: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar
                    size={44}
                    icon={<WarningOutlined />}
                    style={{ background: SINISTER_COLORS.primary, flexShrink: 0 }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0, color: "#242166" }}>
                      Gestion des sinistres
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Suivi des dossiers déclarés
                    </Text>
                  </div>
                </div>

                {(rightsService("HCO022") || rightsService("HCO005") || rightsService("HCO019")) && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate("/declarer-sinister")}
                    style={{
                      background: SINISTER_COLORS.primary,
                      borderColor: SINISTER_COLORS.primary,
                      fontWeight: 600,
                    }}
                  >
                    Déclarer un sinistre
                  </Button>
                )}
              </div>

              {/* ── Cartes de statistiques ─────────────────────── */}
              <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
                {stats(count).map((stat, index) => (
                  <Col key={index} xs={12} sm={8} md={8} lg={24 / stats(count).length}>
                    <StatCard
                      title={stat.title}
                      subtitle={stat.subtitle}
                      value={stat.value}
                      icon={stat.icon}
                      iconBg={SINISTER_COLORS.primary}
                      gradient={SINISTER_COLORS.primary}
                    />
                  </Col>
                ))}
              </Row>

              {/* ── Tableau principal ──────────────────────────── */}
              <Card bordered={false} style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>

                {/* Barre de contrôle unifiée */}
                <div style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                }}>
                  {/* Filtres gauche */}
                  <Space wrap size={8}>
                    <div>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                        Période de déclaration
                      </div>
                      <RangePicker
                        format={dateFormat}
                        onChange={handleDateChange}
                        placeholder={["Début", "Fin"]}
                        size="middle"
                        style={{ width: 240 }}
                      />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                        Branche
                      </div>
                      <Select
                        allowClear
                        placeholder="Toutes les branches"
                        onChange={filteringOnBranches}
                        options={branches}
                        size="middle"
                        style={{ width: 200 }}
                        maxTagCount="responsive"
                      />
                    </div>

                    {LOGGED_USER?.profil?.typeOrganisme === "POOL" && (
                      <div>
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                          Compagnies
                        </div>
                        <Select
                          mode="multiple"
                          allowClear
                          placeholder="Toutes les compagnies"
                          onChange={filteringOnCompanies}
                          options={membres}
                          size="middle"
                          style={{ width: 240 }}
                          maxTagCount="responsive"
                        />
                      </div>
                    )}
                  </Space>

                  {/* Actions droite */}
                  <Space size={8}>
                    <Tooltip title={hasActiveFilters ? `Exercice ${selectedYear}${selectedMonths.length ? ` · ${selectedMonths.length} mois` : ""}` : "Filtrer par exercice / mois"}>
                      <Button
                        icon={<FilterOutlined />}
                        onClick={() => setShowFilters(true)}
                        type={hasActiveFilters ? "primary" : "default"}
                        style={hasActiveFilters ? { background: "#8b5cf6", borderColor: "#8b5cf6" } : {}}
                      >
                        Exercice
                        {hasActiveFilters && (
                          <Badge
                            count={selectedMonths.length || "!"}
                            size="small"
                            style={{ marginLeft: 6, backgroundColor: "#ef4444" }}
                          />
                        )}
                      </Button>
                    </Tooltip>

                    <Divider type="vertical" style={{ height: 28, margin: 0 }} />

                    <Tooltip title="Exporter en PDF (États CIMA)">
                      <Button
                        icon={<FilePdfOutlined />}
                        loading={loadingSinistres}
                        onClick={() => generateSinistresPdf()}
                      >
                        PDF
                      </Button>
                    </Tooltip>

                    {excelPreviewUrl?.length > 0 && (
                      <Tooltip title="Télécharger l'Excel">
                        <Button
                          icon={<FileExcelOutlined />}
                          onClick={() => generateSinistresExcel(excelPreviewUrl)}
                          style={{ color: "#16a34a", borderColor: "#16a34a" }}
                        >
                          Excel
                        </Button>
                      </Tooltip>
                    )}
                  </Space>
                </div>

                {/* Tabs + Table */}
                <div style={{ padding: "0 4px" }}>
                  <ConfigProvider locale={frFR}>
                    <Spin spinning={loadingSinistres} size="large">
                      <Tabs
                        onChange={onTabsChange}
                        activeKey={tabId}
                        className="custom-tabs-pro"
                        size="large"
                        style={{ padding: "0 16px" }}
                        items={tabsDefinition(data, count).map(({ key, label, icon, count: tabCount }) => ({
                          key,
                          label: (
                            <span>
                              {icon}
                              {label}
                              <Badge
                                count={tabCount}
                                className="tab-badge-pro"
                                style={{
                                  backgroundColor: key === tabId ? "white" : "#667eea",
                                  color: key === tabId ? "#667eea" : "white",
                                  marginLeft: 6,
                                }}
                              />
                            </span>
                          ),
                          children: (
                            <div className="tab-content-wrapper-pro">
                              <Table
                                columns={getTabColumns(key)}
                                dataSource={getTabDataSource(key)}
                                onChange={handleChange}
                                rowKey="key"
                                pagination={pagination}
                                size="middle"
                              />
                            </div>
                          ),
                        }))}
                      />
                    </Spin>
                  </ConfigProvider>
                </div>
              </Card>

              {/* ── Modal filtres période ──────────────────────── */}
              {showFilters && (
                <FiltersModal
                  setShowFilters={setShowFilters}
                  styles={styles}
                  hasActiveFilters={hasActiveFilters}
                  applyFilters={applyFilters}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                  selectedMonths={selectedMonths}
                  setSelectedMonths={setSelectedMonths}
                />
              )}

              {/* ── Prévisualisation PDF ───────────────────────── */}
              {pdfPreviewUrl && (
                <div style={styles.pdfPreviewContainer}>
                  <div style={styles.pdfPreviewSubContainer}>
                    <button
                      onClick={() => setPdfPreviewUrl(null)}
                      style={styles.pdfPreviewCloseButton}
                    >
                      Fermer
                    </button>
                    <div className="text-center">PRÉVISUALISATION</div>
                    <iframe
                      src={pdfPreviewUrl}
                      style={{ flexGrow: 1, border: "none" }}
                      title="Aperçu PDF"
                    />
                    <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
                      <a
                        href={pdfPreviewUrl}
                        download={`ETATS_CIMA_${new Date().toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}.pdf`}
                        style={styles.downloadPdfButton}
                      >
                        Télécharger le PDF
                      </a>
                      <button
                        onClick={() => generateSinistresExcel(excelPreviewUrl)}
                        style={styles.downloadExcelButton}
                      >
                        Télécharger l'Excel
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
            <Footer />
            <div className="content-backdrop fade" />
          </div>
        </div>
      </div>
      <div className="drag-target" />
    </div>
  );
};

export default ListSinistres;
